<template>
  <multiselect
    ref="multiselect"
    v-model="selectedTags"
    :options="availableTags"
    :multiple="true"
    :taggable="false"
    :hide-selected="false"
    :show-labels="false"
    :show-no-results="!waitingForSearchToFinish"
    :option-height="130"
    :disabled="false"
    :close-on-select="false"
    open-direction=""
    tag-placeholder=""
    :placeholder="multiselectIsOpened ? 'Search' : 'Select'"
    label="name"
    track-by="id"
    class="tag-multiselect2"
    :class="{ 'show-search': showSearch, 'hide-placeholder-after': selectedTags.length, 'spinner-mode': waitingForSearchToFinish }"
    :searchable="true"
    @search-change="findTagSearchChange"
    @input="valueChanged"
    @open="multiselectIsOpened = true"
    @close="multiselectIsOpened = false" >
    <div
      v-if="$refs.multiselect && $refs.multiselect.filteredOptions.length"
      slot="beforeList"
      class="multiselect-title">
      All tags
    </div>
    <div
      slot="noOptions"
      ref="hideParent" />
    <div
      slot="noResult" >
      <p
        v-if="!waitingForSearchToFinish"
        style="white-space: pre-wrap; text-align: center; line-height: 1.5;" ><span class="emoji-fix inline">😐</span>&nbsp;We couldn't find anything for&nbsp;<b>{{ tagsSearchQuery }}</b></p>
    </div>
    <div
      v-if="waitingForSearchToFinish || (hasNextPage && !waitingForSearchToFinish)"
      slot="afterList"
      class="multiselect-title spinner">
      <div
        v-observe-visibility="endOfTagsList">
        <Spinner
          style="margin-bottom: 8px;"
          :size="19"
          :thickness="2" />
      </div>
    </div>
    <div
      slot="selection"
      slot-scope="{ values, remove }"
      class="tag-scroll-wrapper">
      <div class="scroll multiselect__tags-wrap">
        <span
          v-for="(tag, index) in values"
          :key="index"
          class="multiselect__tag flex flex-align-center"
          :class="{ 'no-edit': tag.icons && tag.icons.dark }" >
          <img
            v-if="tag.icons"
            :src="tag.icons.dark"
            style="height: 11px; margin-right: 7px;" >
          <span>{{ tag.name }}</span>
          <i
            class="multiselect__tag-icon no-shrink"
            @click.stop.prevent="remove(tag)"
            @click.native="deleteTag(index, $event)"
            @mousedown.stop.prevent />
        </span>
      </div>
      <SBIcon
        class="down-arrow"
        :icon="SBIconList.DOWN_ARROW"
        :color="SBColors.greyPrimary"
        :size="11"
        :hover-size="30"/>
    </div>
    <template
      slot="option"
      slot-scope="{ option }" >
      <span
        class="flex flex-align-center"
        :class="{ 'custom-icon': option.icons && option.icons.dark }">
        <img
          v-if="option.icons"
          :src="option.icons.dark"
          style="height: 11px; margin-right: 7px;" >
        <span>{{ option.name }}</span>
      </span>
    </template>
  </multiselect>
</template>


<script>
import { debounce } from 'debounce';
import Multiselect from 'vue-multiselect';
import { mapGetters } from 'vuex';

import api from '@/helpers/api';
import Spinner from '@/components/common/Spinner';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SBColors from '@/helpers/lists/colors';

export default {
  name: 'SBTagMultiselect',
  components: {
    Multiselect,
    Spinner,
    SBIcon,
  },
  props: {
    value: {
      type: Array,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      selectedTags: [],
      availableTags: [],
      maxTagPages: null,
      tagsSearchQueryPreviousValue: '',
      tagsSearchQuery: '',
      multiselectIsOpened: false,
      waitingForSearchToFinish: false,
      tagPage: 0,
      SBIconList,
      SBColors,
    };
  },
  computed: {
    ...mapGetters({
      currentOrganisation: 'organisation/currentOrganisation',
    }),
    hasNextPage() {
      return this.maxTagPages === null || this.tagPage < this.maxTagPages;
    },
    showSearch() {
      return this.availableTags.length > 5 || this.tagsSearchQuery || this.tagsSearchQueryPreviousValue;
    },
  },
  watch: {
    value: {
      handler() {
        if (this.value) {
          this.selectedTags = [...this.value];
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.loadMoreTags(true);
  },
  methods: {
    deleteTag(index, event) {
      event.stopPropagation();
      event.preventDefault();
      this.selectedTags.splice(index, 1);
    },
    endOfTagsList(reached) {
      if (reached && !this.waitingForSearchToFinish) {
        this.loadMoreTags();
      }
    },
    loadMoreTags(reset = false) {
      if ( // When resetting a contact with different business id
        reset
      ) {
        this.tagPage = 0;
        this.maxTagPages = null;
      }

      this.tagPage++;
      if (this.maxTagPages !== null && this.tagPage > this.maxTagPages) {
        this.waitingForSearchToFinish = false;
        return;
      }
      const data = {
        limit: 50,
        page: this.tagPage,
        name: this.tagsSearchQuery,
      };
      api('GET', `organisation/${this.currentOrganisation.id}/contact-tags`, data, null, 'w')
        .then((response) => {
          if (response.status === 200) {
            if ( // When resetting a contact with different business id
              reset
            ) {
              this.availableTags = [...response.data.data];
            } else {
              this.availableTags = [...this.availableTags, ...response.data.data];
            }
            this.maxTagPages = response.data.meta.last_page;
          }
          this.waitingForSearchToFinish = false;
        })
        .catch((error) => {
          this.maxTagPages = 0;
          this.waitingForSearchToFinish = false;
        });
    },
    findTagSearchChange(searchQuery) {
      this.waitingForSearchToFinish = true;
      this.tagsSearchQueryPreviousValue = this.tagsSearchQuery;
      this.tagsSearchQuery = searchQuery;
      this.findTagMatch(searchQuery);
    },
    findTagMatch: debounce(function f(tagsSearchQuery) {
      this.alreadySelected = this.selectedTags.includes(tagsSearchQuery.toLowerCase());
      this.loadMoreTags(true);
    }, 500),
    valueChanged() {
      this.$emit('input', this.selectedTags);
    },
  },
};
</script>

<!--// sb2-bordered sb2-hoverable sb2-focusable form-control -->

<style lang="scss" scoped>
.tag-multiselect2 ::v-deep {
  border-radius: 5px;
  box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
  border: solid 1px $greyBorder;
  min-height: 30px !important;
  flex-wrap: nowrap;
  box-sizing: border-box;
  cursor: pointer !important;

  * {
    cursor: default !important;
  }

  li, li * {
    cursor: pointer !important;
  }

  .multiselect__content-wrapper {
    top: 34px;
    border-width: 0px;
    box-shadow: 0 2px 5px 0 rgba(13, 15, 22, 0.23);

    .multiselect__content {
      width: 100%;

      .multiselect__element {
        position: relative;

        .multiselect__option {
          padding-left: 30px !important;

          span {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          &>span:not(.custom-icon)::after {
            content: '';
            position: absolute;
            mask-image: url(~@/assets/img/SB-Icons/tag.svg);
            mask-size: 12px 12px;
            mask-repeat: no-repeat;
            mask-position: 12px center;
            background-color: #54626c;
            width: 30px;
            height: 100%;
            left: 0;
          }

          &.multiselect__option--selected {
            &:not(:hover) {
              background-color: $white !important;
            }

            span {
              margin-right: 22px;
            }

            &::before {
              content: '';
              position: absolute;
              mask-image: url(~@/assets/img/SB-Icons/done-tick-blue.svg);
              mask-size: 9px 9px;
              mask-repeat: no-repeat;
              mask-position: 12px center;
              background-color: $bluePrimary;
              width: 30px;
              height: 100%;
              right: 7px;
              top: 0;
            }
          }
        }
      }
    }
  }

  .multiselect__tags {
    border: none;
    padding: 0 0 0 0;
    cursor: default;
    min-height: 0;
    cursor: pointer !important;

    * {
      cursor: pointer !important;
    }

    input {
      cursor: default !important;
    }

    .multiselect__tags-wrap {
      max-width: 100%;

      .multiselect__tag {
        cursor: default;
        margin: 0 5px 2px 0;
        padding: 3px 6px 3px 8px;
        border-radius: 11px;
        background-color: #e7f1fe;
        overflow: unset;

        span {
          margin: 0 15px 0 0;
          font-size: 12px !important;
          font-weight: 500;
          line-height: 1.22;
          color: #2680eb;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          display: block;
        }

        .multiselect__tag-icon {
          color: #2680eb;
          margin: 0 !important;
          right: 4px;
          top: 6px;
          bottom: 6px;
          width: 13px;
          background-image: unset !important;
          background-color: $bluePrimary !important;
          mask: url(̌̌~@/assets/img/SB-Icons/close-thick.svg) no-repeat center !important;
          mask-size: 6px 6px !important;

          &::after {
            display: none;
          }
        }

        &.no-edit {
          background-color: #edf0f2;
          span {
            color: $blackPrimary;
          }
          .multiselect__tag-icon {
            background-color: $blackPrimary !important;
          }
        }
      }
    }
  }

  .multiselect__select {
    // We decided not to show any "add tag" icon. If we decide to have it then just delete the line below and add padding-right to multiselect__tags with the value of __select width.
    display: none;

    width: 20px;
    height: 20px;
    right: 0;
    top: 0;
    padding: 0;
    margin: 0;
    mask-image: url(~@/assets/img/SB-Icons/plus.svg);
    mask-size: 8px 8px;
    background-color: $greyPrimary;
  }
}

.tag-multiselect2.show-search ::v-deep {
   .multiselect__content-wrapper {
    border-top: 44px solid #f8fafb !important;

    &::before {
      content: '';
      background-color: $greyUltralight;
      z-index: 99999;
      border-bottom: solid 1px $greyBorderModal;
      width: 100%;
      display: block;
      height: 0px;
      position: sticky;
      top: 0;
    }
  }
}

.tag-multiselect2 {

  &.multiselect--active ::v-deep {
    .multiselect__select {
      transform: rotateZ(90deg);
    }

    .multiselect__input {
      position: absolute;
      top: 34px;
      left: 0px;
      height: 26px;
      z-index: 99999;
      margin: 10px 12px;
      width: calc(100% - 24px) !important;
      padding: 5px 8px;

      box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1) !important;
      border: solid 1px #cfd8dc !important;
      border-radius: 5px !important;

      &:hover {
        box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.3) !important;
      }

      &:focus {
        outline: none !important;
        border: solid 1px #cfd8dc;
        box-shadow: 0 0 0 3px #bfdeff !important;
      }
    }
  }
}

.tag-scroll-wrapper {
  max-width: 100%;
  max-height: 30px;
  overflow: hidden;
}

.scroll {
  overflow-x: scroll;
  display: flex;
  column-gap: 0px;
  padding: 5px 0px 50px 10px;

  span {
    white-space: nowrap;
  }

  & > span {
    &:last-of-type {
      margin-right: 50px !important;
    }
  }
}

.down-arrow {
  position: absolute;
  top: 0px;
  right: 0px;
  background: $white;

  &::before {
    content: '';
    width: 20px;
    left: -20px;
    height: 100%;
    background: linear-gradient(to right, rgba(255,255,255,0) , rgba(255,255,255,1));
    position: absolute;
  }
}

.tag-multiselect2.multiselect--active:not(.hide-placeholder-after) ::v-deep .multiselect__tags:after {
  content: 'Select';
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #9eafb5 !important;
  font-size: 14px !important;
}

.tag-multiselect2.spinner-mode ::v-deep ul {
  > li, > div:not(.spinner) {
    display: none !important;
  }
}

.custom-icon {
  margin-left: -18px;
}
</style>
